import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import type { LanguageCodeForJSA } from '~/shared/internationalization';

import { useSetupLanguage } from '../hooks/use-language';

type ActiveLanguageContextValue = {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly availableLanguages: readonly { readonly id: LanguageCodeForJSA; readonly name: string }[];
    readonly setActiveLanguage: (language: LanguageCodeForJSA) => void;
};

const ActiveLanguageContext = createContext<ActiveLanguageContextValue | null>(null);

type TranslationItem = { readonly translations: readonly { readonly language: LanguageCodeForJSA }[] } | undefined;

type ActiveLanguageProviderProps = {
    readonly preferredLanguage?: LanguageCodeForJSA;
    readonly profiles: readonly TranslationItem[];
};

export const ActiveLanguageProvider: FunctionComponent<PropsWithChildren<ActiveLanguageProviderProps>> = ({
    children,
    preferredLanguage,
    profiles = [],
}) => {
    const { activeLanguage, activeLanguageIndex, availableLanguages, setActiveLanguage } = useSetupLanguage(
        preferredLanguage,
        ...profiles,
    );

    const previousPreferredLanguageRef = useRef(preferredLanguage);

    useEffect(() => {
        if (preferredLanguage === previousPreferredLanguageRef.current) {
            return;
        }

        if (
            is.nonEmptyStringAndNotWhitespace(preferredLanguage) &&
            availableLanguages.some((language) => language.id === preferredLanguage)
        ) {
            previousPreferredLanguageRef.current = preferredLanguage;
            setActiveLanguage(preferredLanguage);
        }
    }, [preferredLanguage, availableLanguages, setActiveLanguage]);

    const value = useMemo(
        () => ({
            activeLanguage,
            activeLanguageIndex,
            availableLanguages,
            setActiveLanguage,
        }),
        [activeLanguage, activeLanguageIndex, availableLanguages, setActiveLanguage],
    );

    return <ActiveLanguageContext.Provider value={value}>{children}</ActiveLanguageContext.Provider>;
};

export const useActiveLanguage = (): ActiveLanguageContextValue => {
    const context = useContext(ActiveLanguageContext);

    if (context === null) {
        throw new Error('useActiveLanguage must be used within an ActiveLanguageProvider');
    }

    return context;
};
